import FileSelection from 'components/FileSelection/FileSelection';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { handleRedirection } from 'utils/utils';
import { Button } from 'primereact/button';
import { detectDiscrepancy } from 'services/fileManager/fileManagerServices';
import useFetchFiles from 'hooks/useFetchFiles';
import { Menu } from 'primereact/menu';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useDiscrepancies } from 'hooks/useDiscrepancies';
import { DiscrepancyHistory } from './DiscrepancyHistory';
import GlobalTooltip from 'components/GlobalTooltip';

export default function AiDiscrepancy() {
  let { type, id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const caseData = location.state || JSON.parse(localStorage.getItem('caseData'));

  const { files, getFiles, handleSetFiles } = useFetchFiles();
  const userContext = useContext(UserDetailsContext);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const menu = useRef(null);
  const { addToast } = useToast();
  const { listDiscrepancies, loadDiscrepancies, refreshDiscrepancies } = useDiscrepancies();
  const getPageLimit = () => {
    if (type === 'cases' && userContext) {
      return userContext?.firmDetails?.pagination_preference?.case?.discrepancies;
    } else if (type !== 'leads' && userContext) {
      return userContext?.firmDetails?.pagination_preference?.lead?.discrepancies;
    } else if (userContext) {
      return userContext?.firmDetails?.pagination_preference?.discrepancies?.self;
    }
  };

  const menuOptions = [
    {
      label: 'as PDF',
      command: () => {
        handleDetectDiscrepancy('pdf');
      },
    },
    {
      label: 'as DOCX',
      command: () => {
        handleDetectDiscrepancy('docx');
      },
    },
  ];

  const handleGetFiles = () => {
    let obj = {
      case_id: id,
    };
    getFiles(obj);
  };

  const toggleSelect = (id) => {
    handleSetFiles((prevFiles) => {
      const updatedFiles = JSON.parse(JSON.stringify(prevFiles));

      const updateNode = (nodes) => {
        Object.keys(nodes)?.forEach((key) => {
          if (nodes[key]?.entry_type === 'file' && nodes[key]?.file_id === id) {
            nodes[key].is_checked_discrepancy = !nodes[key].is_checked_discrepancy;
          } else if (nodes[key]?.entry_type === 'folder' && nodes[key]?.sub_items) {
            updateNode(nodes[key]?.sub_items);
          }
        });
      };
      updateNode(updatedFiles);
      return updatedFiles;
    });
    setSelectedFiles((prevSelected) => {
      const existingIndex = prevSelected?.findIndex((file) => file.file_id === id);

      if (existingIndex !== -1) {
        return prevSelected?.filter((file) => file.file_id !== id);
      } else {
        const findFile = (nodes) => {
          for (const key of Object.keys(nodes)) {
            if (nodes[key]?.entry_type === 'file' && nodes[key]?.file_id === id) {
              return nodes[key];
            } else if (nodes[key]?.entry_type === 'folder' && nodes[key]?.sub_items) {
              const found = findFile(nodes[key]?.sub_items);
              if (found) return found;
            }
          }
          return null;
        };
        const fileToAdd = findFile(files);
        if (fileToAdd) {
          return [...prevSelected, fileToAdd];
        }
        return prevSelected;
      }
    });
  };

  const handleDetectDiscrepancy = (reportFormat = 'pdf') => {
    const fileDetails = selectedFiles?.reduce((acc, file) => {
      acc[file.file_name] = file.file_path;
      return acc;
    }, {});

    const data = {
      case_id: id,
      case_name: caseData?.case_name,
      is_lead: type !== 'cases',
      file_details: fileDetails,
      report_extension: reportFormat,
      timezone: userContext?.userDetails?.timezone,
    };

    detectDiscrepancy(data)
      .then(async (response) => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DISCREPANCY_SUCCESS);

        setSelectedFiles([]);
        handleSetFiles((prevFiles) => {
          const updatedFiles = JSON.parse(JSON.stringify(prevFiles));
          const clearDiscrepancyFlags = (nodes) => {
            Object.keys(nodes)?.forEach((key) => {
              if (nodes[key]?.entry_type === 'file') {
                nodes[key].is_checked_discrepancy = false;
              } else if (nodes[key]?.entry_type === 'folder' && nodes[key]?.sub_items) {
                clearDiscrepancyFlags(nodes[key]?.sub_items);
              }
            });
          };
          clearDiscrepancyFlags(updatedFiles);
          return updatedFiles;
        });

        const limit = getPageLimit() ?? 10;
        await refreshDiscrepancies(limit, '', id);
      })
      .catch((error) => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.DISCREPANCY_FAILED);
      });
  };

  useEffect(() => {
    const extractSelectedFiles = (nodes) => {
      let selected = [];

      Object.keys(nodes)?.forEach((key) => {
        if (nodes[key]?.entry_type === 'file' && nodes[key]?.is_checked_discrepancy) {
          selected.push(nodes[key]);
        } else if (nodes[key]?.entry_type === 'folder' && nodes[key]?.sub_items) {
          selected = [...selected, ...extractSelectedFiles(nodes[key]?.sub_items)];
        }
      });
      return selected;
    };
    setSelectedFiles(extractSelectedFiles(files));
  }, [files]);

  useEffect(() => {
    handleGetFiles();
    return () => {
      if (!window.location.pathname?.includes(`/discrepancy`)) {
        localStorage.removeItem('caseData');
      }
    };
  }, []);

  return (
    <>
      <GlobalLoader />
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center py-2 my-3">
          <div className="pointer">
            <i
              onClick={() => {
                const url = `/${type}/${id}/files`;
                handleRedirection(history, url);
              }}
              className="icon-back icon-box me-3 pointer"
            ></i>
            <label className="sub-title">Discrepancy Detection</label>
          </div>
        </div>

        <div className="row" style={{ maxHeight: '100%' }}>
          <div className="d-flex mt-2 align-items-center mb-0">
            <span className="d-flex p-0 black-700" style={{ lineHeight: '18px', fontSize: '12px' }}>
              <i className="pi pi-info-circle me-2"></i>
              AI-generated responses may contain errors or inaccuracies. You should not rely on AI responses without independent
              verification.
            </span>
          </div>
          <div className="col-lg-4 col-12 p-3" style={{ height: '100%', overflowY: 'auto' }}>
            <div className="d-flex">
              <div className="col-md-12 col-12 d-flex justify-content-between align-items-center">
                <h1 className="caption-bold mb-0">File Selection</h1>
                <div className="d-flex justify-content-end align-items-center">
                  <Menu model={menuOptions} popup ref={menu} />
                  <GlobalTooltip
                    tooltip={!(selectedFiles && selectedFiles?.length >= 2) ? 'Choose at least two files for discrepancy detection' : ''}
                  >
                    <Button
                      id="identify-discrepancy-btn"
                      label="Identify Discrepancy"
                      aria-controls="popup_menu"
                      aria-haspopup
                      className="p-button-rounded p-button-primary"
                      onClick={(event) => menu.current.toggle(event)}
                      disabled={!(selectedFiles && selectedFiles?.length >= 2)}
                    />
                  </GlobalTooltip>
                </div>
              </div>
            </div>
            {Object.keys(files)?.length > 0 ? (
              <div className="mt-3">
                <div className="col-12">
                  <div className="card-bg py-3 overflow-y-auto overflow-x-hidden">
                    <FileSelection files={files} toggleSelect={toggleSelect} isDiscrepancyEntry={true} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="card-bg p-3 mt-3 no-entity-card">
                <div className="row">
                  <div className="col-12">No Files Found</div>
                </div>
              </div>
            )}
          </div>
          <div className="col-lg-8 col-12 p-3">
            <div className="d-flex">
              <div className="col-md-12 col-12 d-flex justify-content-between align-items-center">
                <h1 className="caption-bold mb-0">Task History</h1>
              </div>
            </div>
            <div className="mt-3 timeline-container">
              <DiscrepancyHistory
                isCaseFileManager={type === 'cases'}
                isLeadFileManager={type === 'leads'}
                caseDetails={{
                  case_id: id,
                  case_name: type === 'cases' ? caseData?.case_name : undefined,
                  is_lead: type !== 'cases',
                }}
                listDiscrepancies={listDiscrepancies}
                loadDiscrepancies={loadDiscrepancies}
                refreshDiscrepancies={refreshDiscrepancies}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
